import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Copy, X } from "lucide-react";
import { activeSale, saleDay } from "@/data/sales";

const STORAGE_KEY = "nsSaleBannerHidden";

function wasHidden(code: string) {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === code;
  } catch {
    return false;
  }
}

/** The current sale (see src/data/sales.ts), with a copy-code button. Closing it hides that sale only; the next sale shows again. */
export function SaleBanner() {
  const [params] = useSearchParams();
  const sale = activeSale(saleDay(params.get("sale_date")));
  const [hidden, setHidden] = useState(() => (sale ? wasHidden(sale.code) : false));
  const [status, setStatus] = useState("");

  if (!sale || hidden) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(sale.code);
      setStatus(`Code ${sale.code} copied`);
      setTimeout(() => setStatus(""), 2000);
    } catch {
      setStatus(`Copy didn't work. The code is ${sale.code}`);
    }
  };
  const close = () => {
    setHidden(true);
    try {
      window.localStorage.setItem(STORAGE_KEY, sale.code);
    } catch {
      /* the banner just comes back next visit */
    }
  };

  return (
    <div role="region" aria-label="Current sale" className="relative bg-primary text-primary-foreground">
      <div className="container mx-auto flex flex-wrap items-center justify-center gap-x-4 gap-y-2 py-2.5 pr-10 text-center text-sm">
        <p>{sale.text}</p>
        <span className="inline-flex items-center gap-2 rounded-md border border-dashed border-primary-foreground/70 py-0.5 pl-2.5 pr-0.5">
          <strong className="font-mono tracking-wider">{sale.code}</strong>
          <button
            type="button"
            onClick={copy}
            aria-label={`Copy code ${sale.code}`}
            className="inline-flex items-center gap-1 rounded bg-primary-foreground px-2 py-0.5 text-xs font-semibold text-primary hover:bg-primary-foreground/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-foreground"
          >
            {status.startsWith("Code") ? <Check className="h-3 w-3" aria-hidden="true" /> : <Copy className="h-3 w-3" aria-hidden="true" />}
            {status.startsWith("Code") ? "Copied" : "Copy code"}
          </button>
        </span>
        <Link to={sale.href} className="font-semibold underline underline-offset-4 hover:no-underline">
          {sale.linkText}
        </Link>
      </div>
      <button
        type="button"
        onClick={close}
        aria-label="Hide this sale banner"
        className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded hover:bg-primary-foreground/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-foreground"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
      <span className="sr-only" aria-live="polite">{status}</span>
    </div>
  );
}
